'use es6';

import { Map as ImmutableMap } from 'immutable';
import { createAction, handleActions } from 'redux-actions';
import curry from 'transmute/curry';
import requestStatusType from '../constants/requestStatusTypes';
const {
  UNINITIALIZED,
  PENDING,
  SUCCEEDED,
  FAILED
} = requestStatusType;
const STATUS = 'status';
const VALUE = 'value';
const selectStatus = curry((namespace, state) => state[namespace].get('status'));
const selectStatusIs = curry((status, namespace, state) => selectStatus(namespace, state) === status);
const selectStatusValue = curry((status, namespace, state) => selectStatusIs(status, namespace, state) ? state[namespace].get('value') : null);
const selectIsSucceeded = selectStatusIs(SUCCEEDED);
const selectSucceededValue = selectStatusValue(SUCCEEDED);
const selectIsPending = selectStatusIs(PENDING);
const selectPendingValue = selectStatusValue(PENDING);
export function createReducer({
  pending,
  success,
  failure
}) {
  return handleActions({
    [pending](state, action) {
      return state.set(STATUS, PENDING).set(VALUE, action.payload);
    },
    [success](state, action) {
      return state.set(STATUS, SUCCEEDED).set(VALUE, action.payload);
    },
    [failure](state, action) {
      return state.set(STATUS, FAILED).set(VALUE, action.payload);
    }
  }, ImmutableMap({
    status: UNINITIALIZED,
    value: null
  }));
}
export function createActions(namespace, base) {
  return {
    pending: createAction(`${namespace}.${base}_${PENDING}`),
    success: createAction(`${namespace}.${base}_${SUCCEEDED}`),
    failure: createAction(`${namespace}.${base}_${FAILED}`)
  };
}

// fetch will always create a new request
// ignore succeeded request
// cancel pending request
export function createFetchThunk(namespace, {
  pending,
  success,
  failure
}, fetch) {
  return (...fetchArgs) => (dispatch, getState) => {
    const state = getState();
    const pendingValue = selectPendingValue(namespace, state);
    const cancelPrevious = pendingValue && pendingValue.cancel;
    if (typeof cancelPrevious === 'function') {
      cancelPrevious();
    }
    let canceled = false;
    const cancel = () => canceled = true;
    const promise = fetch(...fetchArgs).then(response => {
      if (!canceled) dispatch(success(response));
      return response;
    }, error => {
      if (!canceled) dispatch(failure(error));
      return error;
    });
    dispatch(pending({
      promise,
      cancel
    }));
    return promise;
  };
}

// ensure will start a new request if there is neither:
// a pending request nor
// a succeeded request
export function createEnsureThunk(namespace, actions, fetch) {
  return (...fetchArgs) => (dispatch, getState) => {
    const state = getState();
    if (selectIsSucceeded(namespace, state)) {
      return Promise.resolve(selectSucceededValue(namespace, state));
    } else if (selectIsPending(namespace, state)) {
      return selectPendingValue(namespace, state).promise;
    } else {
      const fetchThunk = createFetchThunk(namespace, actions, fetch);
      return dispatch(fetchThunk(...fetchArgs));
    }
  };
}